import { getDpopKey } from './dpop-key-store';
import { createDpopProof } from './dpop-proof';
import type { AuthorizationHeaders } from './dpop-headers';

/**
 * DPoP for magic-link sessions. Unlike B2C there is no exchange: the gateway issues the magic-link token
 * itself, so it binds it to this browser's key when the sign-in proves possession of it (a DPoP proof on
 * POST /api/magic-link/verify). A bound token carries `cnf.jkt`; from then on the gateway only accepts it as
 * `Authorization: DPoP <token>` with a fresh proof, including on refresh.
 *
 * Binding is opt-in per sign-in. With the flag off, the kill switch set, or a browser that cannot hold a
 * non-extractable key, verify sends no proof, the gateway issues an unbound token and everything stays Bearer
 * exactly as before. The kill switch only affects new sign-ins: a token that is already bound cannot fall
 * back to Bearer, so its session ends at the next refresh failure and the contact signs in again.
 *
 * Kept free of the auth services (magic-link-auth-service imports it), like dpop-key-store and dpop-proof.
 */

const KILL_SWITCH_KEY = 'gorelo.dpop.disabled';

/** Whether a new magic-link sign-in should ask for a bound token. */
export function isMagicLinkBindingEnabled(): boolean {
    if (import.meta.env.VITE_dpopMagicLinkEnabled !== 'true') return false;
    try {
        if (localStorage.getItem(KILL_SWITCH_KEY) === 'true') return false;
    } catch {
        // Storage unreadable: nothing set the kill switch.
    }
    return typeof window !== 'undefined' && window.isSecureContext
        && !!globalThis.crypto?.subtle && typeof indexedDB !== 'undefined';
}

/** True when the token carries a `cnf.jkt` confirmation, i.e. the gateway bound it to this browser's key. */
export function isBoundToken(token: string | null | undefined): boolean {
    if (!token) return false;
    try {
        const part = token.split('.')[1];
        if (!part) return false;
        const json = atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '='));
        const cnf = JSON.parse(json)?.cnf;
        return typeof cnf?.jkt === 'string' && cnf.jkt.length > 0;
    } catch {
        return false;
    }
}

/** A proof for the sign-in (no access token yet, so no `ath`). */
export async function createSignInProof(method: string, url: string): Promise<string> {
    return createDpopProof(await getDpopKey(), method, url);
}

/**
 * Headers for one magic-link request: `DPoP <token>` plus a proof when the token is bound, otherwise the
 * Bearer header the portal has always sent. There is no Bearer fallback for a bound token - the gateway
 * refuses it. If the key was lost (site data cleared), getDpopKey makes a new one whose proofs the gateway
 * refuses too; the refresh then fails and the contact signs in again. Throws only if WebCrypto/IndexedDB fail.
 */
export async function magicLinkAuthorization(method: string | undefined, url: string, token: string): Promise<AuthorizationHeaders> {
    if (!isBoundToken(token)) return { Authorization: `Bearer ${token}` };
    const proof = await createDpopProof(await getDpopKey(), method ?? 'GET', url, token);
    return { Authorization: `DPoP ${token}`, DPoP: proof };
}
